import { NextApiRequest, NextApiResponse } from 'next';
import { metrics } from '@opentelemetry/api';

/*
 * Prepare Metrics
 */

// serviceName should match /instrumentation.ts
const serviceName = 'olo-web-refactor';
const metricPrefix = 'olo_web_refactor';

// Instance of OTEL metrics
const meter = metrics.getMeter(serviceName);

const buildSha = process.env.NEXT_PUBLIC_BUILD_COMMIT_SHA || '';
const standardLabels: Record<string, any> = {};
if (buildSha) {
  standardLabels.buildSha = buildSha;
}

/*
 * Custom Metric Handlers
 */

export const metricApiHistogram = meter.createHistogram(`${metricPrefix}_api_route_duration`, {
  description:
    'Duration of API calls with route status and method. Manually extending auto-instrumentation as wrapper.',
});
export type APIHandlerFunction = (eq: NextApiRequest, res: NextApiResponse) => Promise<void>;
export const apiMetricWrapper = (
  apiFunc: APIHandlerFunction,
  pathName: string,
  labels: Record<string, any> = {}
) => {
  return async (req: NextApiRequest, res: NextApiResponse) => {
    // res.statusCode appears to default to 200 unless otherwise set
    // unfortunately there is now way to track if a meaningful 200 was applied vs default
    // A thrown exception will likely be set to 200.
    // Resist urge to set res.statusCode = 1;
    const tsStart = performance.now();
    let caughtError: string | undefined;
    let statusCode = 1; // 1 is unknown and while invalid as HTTP Status, valid for metric label
    try {
      await apiFunc(req, res);
      statusCode = res.statusCode ?? 1;
    } catch (e) {
      caughtError = e.name ?? 'Error';
      statusCode = e.response?.status ?? res.statusCode ?? 1;
      throw e;
    } finally {
      const tsDeltaMS = performance.now() - tsStart;
      const metricAttributes: Record<string, any> = {
        ...standardLabels,
        ...labels,
        pathName,
        method: req.method ?? 'UNKNOWN',
        statusCode,
      };
      if (caughtError) {
        metricAttributes.error = caughtError;
      }
      metricApiHistogram.record(tsDeltaMS, metricAttributes);
    }
  };
};

// External API calls: basic service level response code count observation
export const metricExternalApiCall = meter.createCounter(`${metricPrefix}_external_api_call`, {
  description: 'Number of calls to external service by service name and status code.',
});
export const fireExternalApiCallMetric = (serviceName: string, statusCode: number): void => {
  metricExternalApiCall.add(1, {
    ...standardLabels,
    serviceName,
    statusCode,
  });
};
export const axiosResponseMetricHandler = (serviceName: string, response: any): void => {
  try {
    // response may be AxiosResponse or AxiosError
    const statusCodeUntyped = response?.status ?? response?.response?.status ?? 0;
    const statusCode = Number(statusCodeUntyped).valueOf();
    fireExternalApiCallMetric(serviceName, statusCode);
  } catch (_e) {
    // ignore as optional
  }
};

// Complete endpoint metrics, proxy for emagine/nmi success
export const metricCompleteOrder = meter.createCounter(`${metricPrefix}_complete_order`, {
  description: 'Number of calls to complete order endpoint.',
});
export type CompleteStatus = 'success' | 'attempt' | 'error' | 'not-allowed' | 'recaptcha-error';
export const fireCompleteOrderMetric = (
  statusKey: CompleteStatus = 'attempt',
  statusCode = 0
): void => {
  metricCompleteOrder.add(1, {
    ...standardLabels,
    statusKey,
    statusCode,
  });
};

/*
 * Prometheus Conversion Metric Handlers
 */

export const metricTrackError = meter.createCounter(`${metricPrefix}_track_error`, {
  description: 'Grafana RUM conversion TrackError event',
});
export const trackPrometheusError = (actionName: string, amt = 1): void => {
  metricTrackError.add(amt, {
    ...standardLabels,
    actionName,
  });
};

export const metricTrackEvent = meter.createCounter(`${metricPrefix}_track_event`, {
  description: 'Grafana RUM conversion TrackEvent event',
});
export const trackPrometheusEvent = (actionName: string, amt = 1): void => {
  metricTrackEvent.add(amt, {
    ...standardLabels,
    actionName,
  });
};

export const metricIdentifyUser = meter.createCounter(`${metricPrefix}_identify_user`, {
  description: 'Grafana RUM conversion IdentifyUser event',
});
export const identifyPrometheusUser = (actionName: string, amt = 1): void => {
  metricIdentifyUser.add(amt, {
    ...standardLabels,
    actionName,
  });
};

export const metricRemoveUser = meter.createCounter(`${metricPrefix}_remove_user`, {
  description: 'Grafana RUM conversion RemoveUser event',
});
export const removePrometheusUser = (actionName: string, amt = 1): void => {
  metricRemoveUser.add(amt, {
    ...standardLabels,
    actionName,
  });
};
